import { assert, defined, definedMap, mapFilterUndefined } from "@glideapps/ts-necessities";
import uniq from "lodash/uniq";
import {
    asArrayValue,
    asBasePrimitiveValue,
    isLoadedDefined,
    loadedDefinedMap,
} from "../../common/components/component-helpers";
import type { BasePrimitiveValue, ValueListener } from "../../common/components/computation-types";
import { formatLocalizedString } from "../../common/components/localization";
import { areValuesEqual } from "../../common/components/primitives";
import type { SerializedComponent } from "../../common/components/SerializedApp";
import { AppKind } from "../../common/components/SerializedApp";
import type { AppEnvironment, AppOther, PotentialValue, ValueProducer } from "../../common/components/types";
import {
    asString,
    isBasePrimitiveValue,
    isLoadingValue,
    isNotEmpty,
    isPrimitive,
    nullLoadingToUndefined,
} from "../../common/computation-model/data";
import type {
    ArrayContentDescription,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    TableGlideType,
} from "../../common/description";
import {
    ArrayScreenFormat,
    getPrimitiveNonHiddenColumns,
    getSourceColumnPath,
    getTableColumn,
    getTableColumnDisplayName,
    PropertyKind,
    SourceColumnKind,
} from "../../common/description";
import { ChoiceStyle } from "../../component-utils/choice-style";
import { WireComponentKind } from "../../wire/component-kind";
import type { WireAction } from "../../wire/types";
import { isBound } from "../../wire/types";
import {
    getDefaultValuesForComponent,
    makeValidationFlagForProperty,
    setColumnInRowForEditComponent,
} from "../builder-utils";
import type {
    ActionPropertyDescriptor,
    AppDescriptionContext,
    ComponentBuilder,
    ComponentSpecialCaseDescriptor,
    EnumPropertyCase,
    PropertyDescriptor,
    PropertyTableGetter,
} from "../components";
import {
    doesMutatingScreenKindSupportDefaultValues,
    makeDefaultValuePropertyDescriptor,
} from "../components/descriptor-utils";
import {
    ColumnPropertyFlag,
    ColumnPropertyHandler,
    EnumPropertyHandler,
    isRequiredPropertyHandler,
    SwitchPropertyHandler,
} from "../components/property-handlers";
import { PropertySection } from "../components/property-sections";
import {
    getColumnProperty,
    getSourceColumnProperty,
    makeEnumProperty,
    makeSwitchProperty,
} from "../components/property-utils";
import type {
    WireActionBackend,
    WireInflationBackend,
    WireRowComponentInflationBackend,
    WireTableComponentHydrator,
    WireValueGetter,
} from "../components/wire";
import { getPropertyWithColumnFormat } from "../formulas";
import type { SerializedElementBuilder } from "../serialized-component";
import { spreadComponentID } from "../wire/utils";
import type { InlineListBuild } from "./array-content";
import { ArrayContentHandlerBase } from "./array-content";

export interface ChoiceArrayContentDescription extends ArrayContentDescription {
    readonly style: PropertyDescription;
    readonly valueProperty: PropertyDescription;
    readonly defaultValue: PropertyDescription | undefined;
    readonly optionProperty: PropertyDescription;
    readonly displayProperty: PropertyDescription;
    readonly isRequired: PropertyDescription;
    readonly isMulti: PropertyDescription | undefined;
}

function isDefaultValueSupported(appKind: AppKind, mutatingScreenKind: MutatingScreenKind | undefined): boolean {
    return appKind === AppKind.App && doesMutatingScreenKindSupportDefaultValues(mutatingScreenKind);
}

const valuePropertyHandler: ColumnPropertyHandler = new ColumnPropertyHandler(
    "valueProperty",
    "Column",
    [
        ColumnPropertyFlag.Required,
        ColumnPropertyFlag.Editable,
        ColumnPropertyFlag.DefaultCaption,
        ColumnPropertyFlag.EditedInApp,
    ],
    undefined,
    undefined,
    getPrimitiveNonHiddenColumns,
    "string",
    PropertySection.Data
);

const dropdownChoice = {
    value: ChoiceStyle.Dropdown,
    label: "Dropdown",
    icon: "choiceDropdown",
};
const chipsChoice = {
    value: ChoiceStyle.Chips,
    label: "Chips",
    icon: "mt-enum-choice-chips",
};

function makeStylePropertyHandler(styles: readonly EnumPropertyCase<ChoiceStyle>[]) {
    return new EnumPropertyHandler(
        { style: ChoiceStyle.Dropdown },
        "Selection Type",
        "Style",
        styles,
        PropertySection.Design,
        "small-images"
    );
}

const appsStylePropertyHandler = makeStylePropertyHandler([
    dropdownChoice,
    {
        value: ChoiceStyle.Inline,
        label: "Segmented",
        icon: "choiceInline",
    },
    chipsChoice,
    {
        value: ChoiceStyle.RadioButtons,
        label: "Radio Buttons",
        icon: "mt-enum-choice-radio",
    },
]);
const pagesStylePropertyHandler = makeStylePropertyHandler([dropdownChoice, chipsChoice]);

const isMultiPropertyHandler = new SwitchPropertyHandler(
    { isMulti: false },
    "Allow selecting multiple",
    PropertySection.Options
);

function supportsMulti(desc: ChoiceArrayContentDescription | undefined): boolean {
    if (desc === undefined) return false;

    const style = appsStylePropertyHandler.getEnum(desc);
    return style === ChoiceStyle.Dropdown || style === ChoiceStyle.Chips || style === ChoiceStyle.RadioButtons;
}

class SubscribingForcer implements ValueListener<AppOther, AppEnvironment> {
    private readonly _subscribed = new Set<ValueProducer>();

    public force = (vp: ValueProducer): PotentialValue => {
        if (!this._subscribed.has(vp)) {
            this._subscribed.add(vp);
            vp.subscribe(this);
        }
        return vp.current;
    };

    public retire(): void {
        for (const vp of this._subscribed) {
            vp.unsubscribe(this);
        }
    }

    public setDirty(): void {
        return;
    }

    public followPath(vp: ValueProducer, path: readonly string[]): ValueProducer | undefined {
        for (const n of path) {
            const inspector = vp.getObjectInspector(this.force);
            const p = inspector?.getProperty(n);
            if (!isLoadedDefined(p)) return undefined;
            vp = p;
        }
        return vp;
    }
}

export class ChoiceArrayContentHandler extends ArrayContentHandlerBase<ChoiceArrayContentDescription> {
    constructor() {
        super(ArrayScreenFormat.Choice);
    }

    public get isEditor(): boolean {
        return true;
    }

    public get defaultIsTable(): boolean {
        return true;
    }

    public getSupportsSearch(): boolean {
        return false;
    }

    public getSpecialCaseDescriptors(ccc: AppDescriptionContext): readonly ComponentSpecialCaseDescriptor[] {
        return [
            {
                name: "Choice",
                analyticsName: "choice",
                description: "Choose one out of many options",
                img: "componentChoice",
                group: ccc.appKind === AppKind.Page ? "Form Elements" : "Pickers",
                appKinds: "both",
            },
        ];
    }

    public needsCaption(_desc: ChoiceArrayContentDescription | undefined): PropertySection | undefined {
        return PropertySection.Design;
    }

    public getDescriptiveName(
        desc: ChoiceArrayContentDescription,
        caption: string | undefined,
        inlineListHostTable: TableGlideType | undefined
    ): [string, string] | undefined {
        const propertyName = valuePropertyHandler.getColumnName(desc);
        if (propertyName !== undefined && inlineListHostTable !== undefined) {
            const column = getTableColumn(inlineListHostTable, propertyName);
            if (column !== undefined) {
                caption = getTableColumnDisplayName(column);
            }
        }

        if (caption === undefined) return undefined;
        return ["Choice", caption];
    }

    public get needsOutputContext(): boolean {
        return true;
    }

    public get sourcePropertyLabel(): readonly [string, PropertySection] {
        return ["Sheet", PropertySection.Source];
    }

    public makeContentForProcessedArray(
        builder: ComponentBuilder,
        desc: ChoiceArrayContentDescription,
        table: TableGlideType,
        arrayData: SerializedComponent,
        _searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        _uniqueID: string,
        caption: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;

        if (inlineListBuild === undefined) return b.emptyUI;
        const { tables, mutatingScreenKind, outputContext } = inlineListBuild;
        if (outputContext === undefined) return b.emptyUI;

        const propertyName = valuePropertyHandler.getColumnName(desc);
        const optionColumnName = getColumnProperty(desc.optionProperty);
        const displayColumnName = getColumnProperty(desc.displayProperty);

        if (propertyName === undefined || optionColumnName === undefined) return b.emptyUI;

        const value = b.getProperty(propertyName, outputContext, table);

        const nameForChoice = getPropertyWithColumnFormat(builder, table, displayColumnName ?? optionColumnName);

        const getOptionProperty = b.getProperty(optionColumnName, b.getContext, table);
        const choices = b.map(
            b.uniqueValues(b.filter(arrayData, b.isDefined(getOptionProperty)), getOptionProperty),
            b.object({
                name: nameForChoice,
                value: getOptionProperty,
            })
        );
        const setColumnsResult = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            propertyName,
            value,
            undefined,
            tables,
            undefined,
            outputContext
        );
        if (setColumnsResult === undefined) return b.emptyUI;
        const { lets, valueToModify, action, isEnabled } = setColumnsResult;

        const style = b.constant(appsStylePropertyHandler.getEnum(desc));

        const isRequired = isRequiredPropertyHandler.getSwitch(desc);
        const isMulti = supportsMulti(desc) && isMultiPropertyHandler.getSwitch(desc);

        return b.let(
            lets,
            b.ui("Choice", {
                value: valueToModify,
                choices,
                caption,
                onChange: action,
                isEnabled,
                isRequired: b.constant(isRequired),
                isMulti: b.constant(isMulti),
                style,
                pushScreen: builder.makePushChoiceScreen(
                    valueToModify,
                    choices,
                    caption,
                    b.constant(isRequired),
                    isMulti,
                    action
                ),
            })
        );
    }

    public getContentPropertyDescriptors<T extends ChoiceArrayContentDescription>(
        getPropertyTable: PropertyTableGetter | undefined,
        insideInlineList: boolean,
        desc: T | undefined,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean,
        withTransforms: boolean,
        arrayVP: ValueProducer | undefined
    ): readonly PropertyDescriptor[] {
        const options: BasePrimitiveValue[] = [];

        if (arrayVP !== undefined) {
            const forcer = new SubscribingForcer();
            try {
                const array = loadedDefinedMap(forcer.force(arrayVP), asArrayValue);

                const optionSourceColumn = getSourceColumnProperty(desc?.optionProperty);
                if (array !== undefined && optionSourceColumn?.kind === SourceColumnKind.DefaultContext) {
                    for (const vp of array) {
                        const o = forcer.followPath(vp, getSourceColumnPath(optionSourceColumn));
                        if (o === undefined) continue;

                        const current = forcer.force(o);
                        if (isLoadedDefined(current)) {
                            options.push(asBasePrimitiveValue(current));
                        }
                    }
                }
            } finally {
                forcer.retire();
            }
        }

        const properties: PropertyDescriptor[] = [valuePropertyHandler];

        if (isDefaultValueSupported(ccc.appKind, mutatingScreenKind)) {
            // TODO: To support this in Pages we need the equivalent of
            // `arrayVP`.
            const defaultProperty = makeDefaultValuePropertyDescriptor(
                ccc,
                mutatingScreenKind,
                "string",
                undefined,
                false,
                uniq(options)
            );
            if (defaultProperty !== undefined) {
                properties.push(defaultProperty);
            }
        }

        properties.push(
            ...super.getContentPropertyDescriptors(
                getPropertyTable,
                insideInlineList,
                desc,
                ccc,
                mutatingScreenKind,
                isDefaultArrayScreen,
                withTransforms,
                arrayVP
            ),
            isRequiredPropertyHandler,
            ccc.appKind === AppKind.App ? appsStylePropertyHandler : pagesStylePropertyHandler,
            isMultiPropertyHandler,
            {
                kind: PropertyKind.Column,
                property: { name: "optionProperty" },
                section: PropertySection.Source,
                label: "Values",
                required: true,
                editable: true,
                searchable: false,
                getIndirectTable: getPropertyTable,
                getAllowedColumns: getPrimitiveNonHiddenColumns,
                preferredNames: ["option", "options", "choice", "choices"],
                preferredType: "string",
            },
            {
                kind: PropertyKind.Column,
                property: { name: "displayProperty" },
                section: PropertySection.Source,
                label: "Display as",
                required: false,
                editable: true,
                searchable: false,
                emptyByDefault: true,
                getIndirectTable: getPropertyTable,
                getAllowedColumns: getPrimitiveNonHiddenColumns,
                preferredNames: ["option", "options", "choice", "choices", "display"],
                preferredType: "string",
            }
        );

        return properties;
    }

    public getDefaultValues(
        builder: ComponentBuilder,
        desc: ChoiceArrayContentDescription,
        { input }: InputOutputTables
    ): readonly [string, SerializedComponent][] {
        return getDefaultValuesForComponent(builder, [desc.valueProperty, desc.defaultValue], input, "string");
    }

    public getBasicSearchProperties(): readonly string[] {
        return [];
    }

    public getActionDescriptors(): readonly ActionPropertyDescriptor[] {
        return [];
    }

    public needValidation(desc: ChoiceArrayContentDescription): boolean {
        return isRequiredPropertyHandler.getSwitch(desc);
    }

    public makeValidationFlag(
        b: SerializedElementBuilder,
        desc: ChoiceArrayContentDescription,
        outputContext: SerializedComponent,
        table: TableGlideType
    ): SerializedComponent | undefined {
        return makeValidationFlagForProperty(b, outputContext, valuePropertyHandler.getColumnName(desc), table);
    }

    public adjustContentDescriptionAfterUpdate(
        desc: ChoiceArrayContentDescription,
        updates: Partial<ChoiceArrayContentDescription> | undefined,
        tables: InputOutputTables,
        ccc: AppDescriptionContext
    ): ChoiceArrayContentDescription {
        desc = super.adjustContentDescriptionAfterUpdate(desc, updates, tables, ccc);

        if (!supportsMulti(desc) && isMultiPropertyHandler.getSwitch(desc)) {
            // The `isMulti` property is incompatible with `style`, so we have
            // to change one or the other.  We don't want to negate the user's
            // choice, so if they changed `isMulti` then we'll change the
            // style, and vice versa.

            if (updates?.isMulti !== undefined) {
                desc = { ...desc, style: makeEnumProperty(ChoiceStyle.Dropdown) };
            } else {
                desc = { ...desc, isMulti: makeSwitchProperty(false) };
            }
        }

        return desc;
    }

    public inflateContent<T extends ChoiceArrayContentDescription>(
        ib: WireInflationBackend,
        desc: T,
        captionGetter: WireValueGetter | undefined,
        containingRowIB: WireRowComponentInflationBackend | undefined,
        componentID: string | undefined
    ): WireTableComponentHydrator | undefined {
        const valuePropertyName = getColumnProperty(desc.valueProperty);
        if (valuePropertyName === undefined) return undefined;

        const {
            forBuilder,
            adc: { appKind },
        } = ib;

        const [valueGetter] = defined(containingRowIB).getValueGetterForProperty(desc.valueProperty, false, {
            inOutputRow: true,
        });

        const [choiceValueGetter] = ib.getValueGetterForProperty(desc.optionProperty, false);
        let choiceNameGetter: WireValueGetter;
        if (desc.displayProperty !== undefined) {
            choiceNameGetter = ib.getValueGetterForProperty(desc.displayProperty, true)[0];
        } else {
            choiceNameGetter = ib.getValueGetterForProperty(desc.optionProperty, true)[0];
        }

        let defaultValueGetter: WireValueGetter | undefined;
        if (isDefaultValueSupported(appKind, ib.mutatingScreenKind) && desc.defaultValue !== undefined) {
            defaultValueGetter = ib.getValueGetterForProperty(desc.defaultValue, false)[0];
        }

        const isMulti = supportsMulti(desc) && isMultiPropertyHandler.getSwitch(desc);
        const isRequired = isRequiredPropertyHandler.getSwitch(desc);
        const style = pagesStylePropertyHandler.getEnum(desc);

        return (thb, chb) => {
            const data = thb.tableScreenContext;
            if (data.length === 0) return undefined;

            assert(chb !== undefined);

            let selectedValue = valueGetter(chb);
            // We set the empty string to `undefined` here so that we don't
            // display the em-dash for empty values.
            if (!isBasePrimitiveValue(selectedValue) || selectedValue === "") {
                selectedValue = undefined;
            }

            // We only set this if `isMulti`
            let selectedStrings: readonly string[] | undefined;
            if (isMulti) {
                if (selectedValue !== undefined) {
                    selectedStrings = Array.from(new Set(asString(selectedValue).split(",")));
                } else {
                    selectedStrings = [];
                }
            }

            const selectedValueOnChangeToken = chb.registerOnValueChange(valuePropertyName);

            const namesForValues = new Map<BasePrimitiveValue, string>();
            for (const row of data) {
                const rhb = thb.makeHydrationBackendForRow(row);

                const value = choiceValueGetter(rhb);
                if (!isBasePrimitiveValue(value)) continue;
                if (namesForValues.has(value)) continue;

                const displayAs = nullLoadingToUndefined(choiceNameGetter(rhb));
                namesForValues.set(value, asString(displayAs));
            }

            function makeOnChangeAction(v: BasePrimitiveValue | (() => BasePrimitiveValue)): WireAction {
                const token = defined(chb).registerAction(async ab => {
                    if (selectedValueOnChangeToken === undefined) return false;
                    if (typeof v === "function") {
                        v = v();
                    }
                    ab.valueChanged(selectedValueOnChangeToken, v);
                    return true;
                });
                return { token };
            }

            let haveSelected = false;
            const valuesAndNamesArray = Array.from(namesForValues);
            const selectedValues = new Set(
                mapFilterUndefined(valuesAndNamesArray, ([v]) => {
                    if (selectedStrings !== undefined) {
                        if (selectedStrings.some(s => areValuesEqual(s, v))) {
                            return v;
                        }
                    } else {
                        if (areValuesEqual(selectedValue, v) === true) {
                            return v;
                        }
                    }
                    return undefined;
                })
            );
            const items = valuesAndNamesArray.map(([v, n]) => {
                const isSelected = selectedValues.has(v);
                if (isSelected) {
                    haveSelected = true;
                }

                let onChange: WireAction | undefined;
                if (isMulti) {
                    if (!isSelected) {
                        onChange = makeOnChangeAction(() =>
                            mapFilterUndefined(valuesAndNamesArray, ([w]) => {
                                if (v === w || selectedValues.has(w)) {
                                    return asString(w);
                                } else {
                                    return undefined;
                                }
                            }).join(",")
                        );
                    } else if (selectedValues.size > 1 || !isRequired) {
                        onChange = makeOnChangeAction(() =>
                            Array.from(selectedValues)
                                .filter(w => w !== v)
                                .join(",")
                        );
                    }
                } else {
                    if (!isSelected) {
                        onChange = makeOnChangeAction(v);
                    } else if (!isRequired) {
                        onChange = makeOnChangeAction("");
                    }
                }
                return { displayAs: n, isSelected, onChange };
            });
            const hasValue = isBound(selectedValue) && isNotEmpty(selectedValue);
            const isValid = haveSelected || (!hasValue && !isRequired);

            // ##ncmDefaultValues:
            // The way we're using state here is quite tricky: `hadValue`
            // should be set if we've ever had a valid value for this
            // component.  If we haven't, and we have a default value, we set
            // the choice to the default value.  The only time we change this
            // state in a follow-up is when setting the default value.  The
            // other two times it's set implicitly via the `getState` default
            // value.
            if (hasValue && isValid) {
                // If this is the first time we `getValue`, then the value
                // will always be true, and we'll never use the default value.
                chb.getState("hadValue", (v): v is boolean => typeof v === "boolean", true);
            }

            let followUp: ((ab: WireActionBackend) => void) | undefined;
            if ((!hasValue || !isValid) && selectedValueOnChangeToken !== undefined) {
                const defaultValue = defaultValueGetter?.(chb);
                if (
                    isBound(defaultValue) &&
                    !isLoadingValue(defaultValue) &&
                    isPrimitive(defaultValue) &&
                    valuesAndNamesArray.some(([v]) => v === defaultValue)
                ) {
                    // Here we know that we have a default value that we can
                    // set, so we check whether the state is `true` by getting
                    // it with a default value of `false`.  Note that we
                    // immediately set it to `true` in the follow-up.
                    const hadValue = chb.getState("hadValue", (v): v is boolean => typeof v === "boolean", false);
                    if (!hadValue.value) {
                        followUp = ab => {
                            ab.valueChanged(selectedValueOnChangeToken, defaultValue);
                            ab.valueChanged(hadValue.onChangeToken, true);
                        };
                    }
                }
            }

            if (!isRequired && style !== ChoiceStyle.Chips) {
                items.unshift({
                    displayAs: "—",
                    isSelected: selectedValue === undefined,
                    onChange: makeOnChangeAction(""),
                });
            }

            const numSelected = items.filter(i => i.isSelected).length;
            let selectedValueText: string;
            if (numSelected === 0) {
                selectedValueText = isRequired ? "Choose something" : "—";
            } else if (numSelected === 1) {
                selectedValueText = defined(items.find(i => i.isSelected)).displayAs;
            } else {
                selectedValueText = formatLocalizedString(numSelected <= 4 ? "twoToFourItems" : "numberOfItems", [
                    numSelected.toString(),
                ]);
            }

            return {
                component: {
                    kind: WireComponentKind.List,
                    ...spreadComponentID(componentID, forBuilder),
                    format: ArrayScreenFormat.Choice,
                    componentTitle: definedMap(nullLoadingToUndefined(captionGetter?.(chb)), asString) ?? "",
                    style,
                    items,
                    selectedValueText,
                    isRequired,
                    isMulti: false,
                },
                isValid,
                hasValue,
                followUp,
            };
        };
    }
}
